import React from 'react';
import { IMsalContext, useMsal } from '@azure/msal-react';
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IDataSampleSummaryItem } from '../../../Models/IDataSampleSummaryItem';
import { ApiService } from '../../../Services/ApiService';
import { hasRole } from "../../../Services/Global";
import { IUnitConfiguration } from '../../../Models/IUnitConfiguration';
import { saveAs } from 'file-saver';
import { DataSampleList } from './DataSampleList';
import { Roles } from '../../../Models/Enums';
import { PositionSelection } from '../../Atom/PositionSelection/PositionSelection';
import { getUnitConfigurationFromConfigurationKey } from '../../../Utils/UnitConfigurationHelper';

// Material UI imports
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useTheme
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CancelIcon from '@mui/icons-material/Cancel';

/**
 * ModelerOverview component - Material UI version
 * Modeler overview component to display the modeler overview page
 */
export const ModelerOverview: React.FC<{}> = () => {
    const ctx: IMsalContext = useMsal();
    const apiService = useMemo(() => new ApiService(ctx), [ctx]);
    const theme = useTheme();

    const [uploading, setUploading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [loadingDataSummary, setLoadingDataSummary] = useState<boolean>(false);
    const [configLoading, setConfigLoading] = useState<boolean>(true);
    const [downloadingSample, setDownloadingSample] = useState<string>('');

    const [unitConfiguration, setUnitConfiguration] = useState<IUnitConfiguration>();
    const [fullConfiguration, setFullConfiguration] = useState<IUnitConfiguration>();

    const [dataSummaryList, setDataSummaryList] = useState<IDataSampleSummaryItem[]>();

    const [csvFile, setCsvFile] = useState<File>();
    const [fileName, setFileName] = useState('Select csv file...');

    const inputFile = createRef<HTMLInputElement>();

    // Invalidates in-flight sample fetches when configuration changes so a stale
    // response cannot overwrite a newer one during rapid position switching.
    const sampleFetchTokenRef = useRef(0);

    // Styles matching BlindTestOverview patterns
    const styles = {
        container: {
            width: '100%',
        },
        headerStack: {
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            paddingInline: theme.spacing(2),
            paddingTop: theme.spacing(2),
        },
        titleSection: {
            flexGrow: 1,
        },
        title: {
            margin: theme.spacing(2),
            fontSize: '1.5rem',
            fontWeight: 'normal',
        },
        actionsSection: {
            margin: theme.spacing(2),
        },
        actionStack: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
        },
        loadingContainer: {
            display: 'flex',
            alignItems: 'center',
            padding: '5px',
        },
        subtitleContainer: {
            textAlign: "center",
            mb: 2
        },
        subtitle: {
            fontWeight: "bold",
            mt: "30px"
        },
        // Mirrors the outer wrapping used inside PositionSelection
        // (`<Box p=3> / <Stack maxWidth=98% mx=auto> / <Paper p=3>`) so the
        // sample list aligns horizontally with the filters above it.
        dataSamplesOuter: {
            padding: 3,
        },
        dataSamplesStack: {
            width: '100%',
            maxWidth: '98%',
            mx: 'auto',
        },
        dataSamplesPaper: {
            padding: 3,
            width: '100%',
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            boxShadow: theme.shadows[2],
        },
        dataSamplesLabel: {
            mb: 3,
            fontWeight: 500,
            color: theme.palette.text.primary
        },
        noDataContainer: {
            display: 'flex',
            justifyContent: 'center',
            padding: 4
        },
        noDataText: {
            color: theme.palette.text.secondary,
            textAlign: 'center'
        },
        instructionsContainer: {
            display: 'flex',
            justifyContent: 'center',
            padding: 3
        },
        instructionsText: {
            textAlign: 'center',
            color: theme.palette.text.secondary
        },
        uploadButton: {
            marginLeft: '10px',
            textTransform: 'none',
        },
        downloadButton: {
            marginLeft: '10px',
            textTransform: 'none',
        },
        centerLoadingContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            padding: 4
        },
        loadingText: {
            color: theme.palette.text.secondary
        },
        fileButton: {
            textTransform: 'none',
        }
    } as const;

    /**
     * Fetches the data sample summary for the given unit configuration.
     * Uses a monotonic token so stale responses are ignored if the user
     * switched positions while a request was in flight.
     */
    const getDataSamplesAsync = useCallback(async (configuration: IUnitConfiguration | undefined) => {
        if (!configuration || !configuration.alfaLavalPosition) return;

        const token = ++sampleFetchTokenRef.current;
        setLoadingDataSummary(true);
        try {
            const response = await apiService.getAsync('modeling/datasamples/summary', configuration);
            if (!response.ok) {
                throw new Error('Could not fetch existing data samples. ' + response.statusText);
            }

            const result: Record<string, IDataSampleSummaryItem> = await response.json();
            if (token !== sampleFetchTokenRef.current) return; // stale response

            const summaries: IDataSampleSummaryItem[] = Object.keys(result).map(key => result[key]);
            setDataSummaryList(summaries);
        } finally {
            if (token === sampleFetchTokenRef.current) {
                setLoadingDataSummary(false);
            }
        }
    }, [apiService]);

    /**
     * Fetches the full unit configuration (used for download/upload metadata).
     */
    const getFullConfig = useCallback(async (configuration: IUnitConfiguration) => {
        const configResponse = await apiService.getAsync('settings/unitconfiguration/alfalavalposition', configuration);
        if (!configResponse.ok) {
            throw new Error('Could not get configuration.');
        }
        const config = await configResponse.json();
        setFullConfiguration(config[0]);
    }, [apiService]);

    useEffect(() => {
        if (!unitConfiguration) return;
        getFullConfig(unitConfiguration);
        getDataSamplesAsync(unitConfiguration);
    }, [unitConfiguration, getFullConfig, getDataSamplesAsync]);

    /**
     * Handles the change event for the position selection. Only updates
     * unit configuration state; the actual data fetch is triggered by the
     * effect above so we never issue the same request twice per selection.
     */
    const handleChange = (positionKey: string) => {
        const c = getUnitConfigurationFromConfigurationKey(positionKey);
        setUnitConfiguration(c);
    }

    /**
     * Uploads the csv file - opens file dialog and uploads immediately
     *
     */
    const uploadCsv = () => {
        inputFile.current?.click();
    }

    /**
     * Submits the csv file to the server for processing.
     */
    const submitCsv = async () => {
        if (csvFile === undefined || unitConfiguration === undefined) return;

        const formData: FormData = new FormData();
        formData.append("file", csvFile, csvFile.name);

        setUploading(true);
        try {
            const response = await apiService.postAsync(formData, 'modeling/sample/csv', fullConfiguration);
            if (!response.ok) {
                let error = '';
                if (response.status === 400) {
                    error = await response.text();
                }
                throw new Error(`${response.statusText} ${error}`);
            }
            await getDataSamplesAsync(unitConfiguration);
            clearCsv();
        } finally {
            setUploading(false);
        }
    }

    /**
     * Handles the file change event for the csv file upload input field 
     *
     * @param {React.FormEvent<HTMLInputElement>} e
     */
    const handleFileChange = (e: React.FormEvent<HTMLInputElement>) => {
        const uploadedFiles: FileList | null = e.currentTarget.files;
        if (!uploadedFiles || uploadedFiles.length === 0) return;

        const f = uploadedFiles[0];
        setFileName(f.name);
        setCsvFile(f);
    }

    /**
     * Generates a csv template for the current configuration and downloads it.
     */
    async function generateCSV() {
        if (fullConfiguration === undefined) return;

        setDownloading(true);
        try {
            const response = await apiService.getAsync('modeling/generatecsv', fullConfiguration);
            if (!response.ok) {
                throw new Error('Could not fetch csv sample. ' + response.statusText);
            }
            await convertStreamToCsv(response.body!, fullConfiguration.processStage + fullConfiguration.alfaLavalPosition + ".csv");
        } finally {
            setDownloading(false);
        }
    }

    /**
     * Converts a readable stream to a CSV blob and triggers a download.
     */
    async function convertStreamToCsv(stream: ReadableStream, filename: string) {
        const reader = stream.getReader();
        let csvData = '';
        while (true) {
            const result = await reader.read();
            if (result.done) {
                break;
            }
            csvData += new TextDecoder().decode(result.value);
        }
        const blob = new Blob([csvData], { type: 'text/csv' });
        saveAs(blob, filename);
    }

    /**
     * Downloads a single data sample as CSV.
     */
    async function downloadDataSampleAsCsv(dataSampleId: string) {
        setDownloadingSample(dataSampleId);
        try {
            const response = await apiService.getAsync(`modeling/datasamples/download/${dataSampleId}`);
            if (!response.ok) {
                throw new Error('Could not fetch csv sample. ' + response.statusText);
            }
            await convertStreamToCsv(response.body!, dataSampleId + ".csv");
        } finally {
            setDownloadingSample('');
        }
    }

    /**
     * Clear CSV file - matching BlindTestOverview pattern
     */
    const clearCsv = () => {
        setCsvFile(undefined);
        setFileName('Select csv file...');
        if (inputFile.current !== null && inputFile.current !== undefined) {
            inputFile.current!.value = '';
        }
    };

    // Derived view state – single source of truth used across the render tree.
    const hasCompleteSelection = !!unitConfiguration?.alfaLavalPosition;
    const hasSamples = !!dataSummaryList && dataSummaryList.length > 0;

    return (
        <Box sx={styles.container}>
            {/* Header with Upload button moved to actions area */}
            <Box sx={styles.headerStack}>
                <Box sx={styles.titleSection}>
                    <Typography variant="h4" component="h3" sx={styles.title}>
                        Modeling
                    </Typography>
                </Box>
                
                <Box sx={styles.actionsSection}>
                    {unitConfiguration?.alfaLavalPosition && (
                        <Box sx={styles.actionStack}>
                            {/* Loading indicator */}
                            {(downloading || uploading) && (
                                <Box sx={styles.loadingContainer}>
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                    <Typography variant="body2">
                                        {downloading ? "Downloading" : "Uploading"}
                                    </Typography>
                                </Box>
                            )}
                            
                            {/* File selection */}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <input 
                                    ref={inputFile} 
                                    type='file' 
                                    hidden 
                                    accept='.csv' 
                                    onChange={handleFileChange} 
                                />
                                {fileName !== 'Select csv file...' && (
                                    <Tooltip title="Remove file">
                                        <IconButton 
                                            size="small" 
                                            onClick={clearCsv}
                                            sx={{ color: theme.palette.error.main }}
                                        >
                                            <CancelIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Button
                                    startIcon={<CloudUploadIcon />}
                                    onClick={uploadCsv}
                                    disabled={downloading}
                                    sx={styles.fileButton}
                                    variant="text"
                                >
                                    {fileName}
                                </Button>
                            </Box>
                            
                            {/* Upload button - only show when file is selected */}
                            {hasRole(...[Roles[Roles.Modeler]]) && csvFile && (
                                <Button
                                    startIcon={<CloudUploadIcon />}
                                    variant="outlined"
                                    onClick={submitCsv}
                                    disabled={uploading || loadingDataSummary || downloading}
                                    sx={styles.uploadButton}
                                >
                                    Upload csv
                                </Button>
                            )}
                            
                            {/* Download button */}
                            <Button
                                startIcon={<DownloadIcon />}
                                variant="contained"
                                color="primary"
                                disabled={downloading}
                                onClick={() => generateCSV()}
                                sx={styles.downloadButton}
                            >
                                Download csv template
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Position Selection owns its own internal loading UX and reports
                the initial configuration phase upwards so we can coordinate a
                single view-level loading sequence: config -> filters -> list. */}
            <PositionSelection
                onChange={handleChange}
                onIncompleteUnitConfiguration={() => { setDataSummaryList([]); setUnitConfiguration(undefined); }}
                onConfigLoadingChange={setConfigLoading}
            />

            {/* Sample list loading: shown only after filters are ready and a
                position is selected; replaces any list content while fetching. */}
            {!configLoading && hasCompleteSelection && loadingDataSummary && (
                <Box sx={styles.centerLoadingContainer}>
                    <CircularProgress size={40} />
                    <Typography variant="body1" sx={styles.loadingText}>
                        Fetching data samples...
                    </Typography>
                </Box>
            )}

            {/* Data samples section: only rendered once we have a complete
                selection and we are NOT currently loading. The wrapping
                mirrors PositionSelection's outer layout so the list is
                aligned with the filters above it. */}
            {!configLoading && hasCompleteSelection && !loadingDataSummary && hasSamples && (
                <Box sx={styles.dataSamplesOuter}>
                    <Stack sx={styles.dataSamplesStack}>
                        <Paper elevation={2} sx={styles.dataSamplesPaper}>
                            <Typography variant="body1" sx={styles.dataSamplesLabel}>
                                Data samples ({dataSummaryList!.length})
                            </Typography>
                            <DataSampleList
                                dataSamples={dataSummaryList!}
                                downloadDataSampleAsCsv={downloadDataSampleAsCsv}
                                downloadingSample={downloadingSample}
                                unitInputConfig={unitConfiguration!}
                                onDelete={() => { getDataSamplesAsync(unitConfiguration) }}
                            />
                        </Paper>
                    </Stack>
                </Box>
            )}

            {!configLoading && hasCompleteSelection && !loadingDataSummary && !hasSamples && (
                <Box sx={styles.noDataContainer}>
                    <Typography variant="body1" sx={styles.noDataText}>
                        No data available for selected position
                    </Typography>
                </Box>
            )}

            {/* Instructions for incomplete configuration */}
            {!configLoading && hasRole(Roles[Roles.Modeler]) && !hasCompleteSelection && (
                <Box sx={styles.instructionsContainer}>
                    <Typography variant="body1" sx={styles.instructionsText}>
                        Select all parameters before uploading data samples.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}; 